import { createElement, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Minus, Plus } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button } from '../common/Button';
import { ErrorBanner } from '../common/ErrorBanner';
import { Input } from '../common/Input';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { MAX_EVENT_PARTY_SIZE } from '../../schemas/firestoreSchemas';
import { EMAIL_SHAPE } from '../../utils/peopleLists';
import { usePlanEventsStore } from '../../store/planEventsStore';
import type { RsvpError, RsvpResult } from '../../api/planEvents';

const NAME_MAX = 80;
const EMAIL_MAX = 254;

// Off screen, not display:none: a bot that skips hidden fields still sees this
// one, while a person and a screen reader never reach it (aria-hidden,
// tabIndex -1, no autofill). The DOM name is deliberately not a real field
// word: Chrome autofills inputs called "website", "url" and the like for a
// person, who would then get a fake success and no sign-up. The callable's
// request key is still `website`; that is the server contract.
export const HONEYPOT_FIELD_NAME = 'redi_hp_field';

const HONEYPOT_STYLE = {
  position: 'absolute',
  left: -10000,
  top: 'auto',
  width: 1,
  height: 1,
  overflow: 'hidden',
} as const;

/**
 * The public sign-up: a name, an email and how many people, no account. Sends
 * through the rsvpToPlanEvent callable; the server checks everything again.
 */
export function EventSignupForm({
  eventId,
  onSignedUp,
}: {
  eventId: string;
  onSignedUp: (result: RsvpResult) => void;
}) {
  const { colors } = useTheme();
  const rsvp = usePlanEventsStore((s) => s.rsvp);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [partySize, setPartySize] = useState(1);
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameProblem = name.trim() ? undefined : 'Add your name.';
  const emailProblem = EMAIL_SHAPE.test(email.trim()) ? undefined : 'Add an email address.';

  const handleSubmit = async () => {
    setSubmitted(true);
    setError(null);
    if (nameProblem || emailProblem || sending) return;
    setSending(true);
    try {
      const result = await rsvp({
        eventId,
        name: name.trim(),
        email: email.trim(),
        partySize,
        website,
      });
      onSignedUp(result);
    } catch (e) {
      setError((e as RsvpError).message ?? "That didn't go through. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={styles.form} testID="event-signup-form">
      <Input
        label="Name"
        value={name}
        onChangeText={(text) => setName(text.slice(0, NAME_MAX))}
        autoCapitalize="words"
        autoComplete="name"
        textContentType="name"
        maxLength={NAME_MAX}
        error={submitted ? nameProblem : undefined}
        testID="event-signup-name"
        containerStyle={styles.field}
      />
      <Input
        label="Email"
        value={email}
        onChangeText={(text) => setEmail(text.slice(0, EMAIL_MAX))}
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="email"
        textContentType="emailAddress"
        maxLength={EMAIL_MAX}
        error={submitted ? emailProblem : undefined}
        testID="event-signup-email"
        containerStyle={styles.field}
      />

      <View style={styles.field}>
        <Text style={[styles.stepperLabel, { color: colors.textSecondary }]}>
          How many people, including you
        </Text>
        {/* A group, not role "adjustable": that becomes role="slider" on web, which
            axe rejects without aria-valuenow, and these are two buttons and a
            number, not a slider. */}
        <View style={styles.stepper} role="group" aria-label="How many people, including you">
          <Pressable
            onPress={() => setPartySize((n) => Math.max(1, n - 1))}
            disabled={partySize <= 1}
            accessibilityRole="button"
            accessibilityLabel="Fewer people"
            testID="event-signup-fewer"
            style={[
              styles.stepButton,
              CURSOR_POINTER,
              { borderColor: colors.border, opacity: partySize <= 1 ? 0.4 : 1 },
            ]}>
            <Minus size={16} color={colors.text} />
          </Pressable>
          <Text
            style={[styles.stepValue, { color: colors.text }]}
            testID="event-signup-party"
            accessibilityLiveRegion="polite">
            {partySize}
          </Text>
          <Pressable
            onPress={() => setPartySize((n) => Math.min(MAX_EVENT_PARTY_SIZE, n + 1))}
            disabled={partySize >= MAX_EVENT_PARTY_SIZE}
            accessibilityRole="button"
            accessibilityLabel="More people"
            testID="event-signup-more"
            style={[
              styles.stepButton,
              CURSOR_POINTER,
              {
                borderColor: colors.border,
                opacity: partySize >= MAX_EVENT_PARTY_SIZE ? 0.4 : 1,
              },
            ]}>
            <Plus size={16} color={colors.text} />
          </Pressable>
        </View>
      </View>

      {Platform.OS === 'web' && (
        <View aria-hidden pointerEvents="none" style={styles.honeypotWrap}>
          {createElement('input', {
            type: 'text',
            name: HONEYPOT_FIELD_NAME,
            id: HONEYPOT_FIELD_NAME,
            value: website,
            onChange: (e: { target: { value: string } }) => setWebsite(e.target.value),
            tabIndex: -1,
            autoComplete: 'off',
            'aria-hidden': true,
            'data-testid': 'event-signup-honeypot',
            style: HONEYPOT_STYLE,
          })}
        </View>
      )}

      <Text style={[styles.privacy, { color: colors.textSecondary }]}>
        The organizer will see your name and email so they can reach you about this event.
      </Text>

      <ErrorBanner message={error} style={styles.error} testID="event-signup-error" />

      <Button
        title="Sign up"
        onPress={() => void handleSubmit()}
        loading={sending}
        fullWidth
        testID="event-signup-submit"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: SPACING.xs,
  },
  field: {
    marginBottom: SPACING.sm,
  },
  stepperLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
  },
  stepButton: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepValue: {
    ...withWeight(TYPOGRAPHY.bodyLarge, 'semibold'),
    minWidth: 24,
    textAlign: 'center',
  },
  honeypotWrap: {
    position: 'absolute',
    height: 0,
    width: 0,
    overflow: 'hidden',
  },
  privacy: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.sm,
  },
  error: {
    marginBottom: SPACING.sm,
  },
});
